/**
 * Dialog for configuring operation mode (local vs remote)
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { useConfig } from "@/lib/config/config-context";
import { AppMode } from "@/lib/config/types";
import { configureTenantsSDK } from "@/lib/harmony-sdk/config";
import { Laptop, Cloud, AlertTriangle } from "lucide-react";

interface ModeConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ModeConfigDialog({
  open,
  onOpenChange,
}: ModeConfigDialogProps) {
  const { config, updateConfig } = useConfig();
  const [selectedMode, setSelectedMode] = useState<AppMode>(config.mode);
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = () => {
    setIsLoading(true);
    try {
      updateConfig({ mode: selectedMode });

      // Reconfigure SDK with new mode
      setTimeout(() => {
        configureTenantsSDK();
      }, 100);

      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update mode:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedMode(config.mode);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure Operation Mode</DialogTitle>
          <DialogDescription>
            Choose how the application connects to the Harmony tenants service
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <RadioGroup
            value={selectedMode}
            onValueChange={(value) => {
              setSelectedMode(value as AppMode);
            }}
            className="space-y-4"
          >
            {/* Remote Mode */}
            <Card
              className={
                selectedMode === AppMode.REMOTE ? "ring-primary ring-2" : ""
              }
            >
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value={AppMode.REMOTE} id="remote" />
                  <Label
                    htmlFor="remote"
                    className="flex cursor-pointer items-center gap-2 font-medium"
                  >
                    <Cloud className="h-4 w-4" />
                    Remote Mode (Production)
                  </Label>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <CardDescription className="space-y-2">
                  <div>
                    Connect to the remote Harmony API server with full
                    authentication.
                  </div>
                  <div className="text-sm">
                    <strong>Features:</strong>
                    <ul className="ml-2 list-inside list-disc space-y-1">
                      <li>Descope SSO authentication required</li>
                      <li>Access to production tenant data</li>
                      <li>Full security and permissions</li>
                      <li>Multi-tenant support</li>
                    </ul>
                  </div>
                </CardDescription>
              </CardContent>
            </Card>

            {/* Local Mode */}
            <Card
              className={
                selectedMode === AppMode.LOCAL ? "ring-primary ring-2" : ""
              }
            >
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value={AppMode.LOCAL} id="local" />
                  <Label
                    htmlFor="local"
                    className="flex cursor-pointer items-center gap-2 font-medium"
                  >
                    <Laptop className="h-4 w-4" />
                    Local Mode (Development)
                  </Label>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <CardDescription className="space-y-2">
                  <div>
                    Connect to a local development server without
                    authentication.
                  </div>
                  <div className="text-sm">
                    <strong>Features:</strong>
                    <ul className="ml-2 list-inside list-disc space-y-1">
                      <li>No authentication required</li>
                      <li>Uses x-tenant-id header for tenant selection</li>
                      <li>Connect to localhost development server</li>
                      <li>Fast development iteration</li>
                    </ul>
                  </div>
                </CardDescription>
              </CardContent>
            </Card>
          </RadioGroup>

          {selectedMode === AppMode.LOCAL && (
            <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" />
              <div className="space-y-1">
                <div className="font-medium text-yellow-800 dark:text-yellow-200">
                  Local Development Mode
                </div>
                <div className="text-sm text-yellow-700 dark:text-yellow-300">
                  Make sure your local Harmony backend server is running on the
                  configured URL. You'll need to set a tenant ID for API
                  requests to work properly.
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isLoading || selectedMode === config.mode}
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
